import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import bg from "../../assets/first_bg.png";
import { API_BASE_URL } from "../../config/apiConfig";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import SignUpWithGoogle from "../../components/GoogleSignUp";

const SignupPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Detect teacher invite code from URL or localStorage
    const params = new URLSearchParams(location.search);
    const teacherCodeFromUrl = params.get("teacher_code") || "";
    const teacherCodeFromStorage = localStorage.getItem("pending_teacher_code") || "";
    const teacherCode = teacherCodeFromUrl || teacherCodeFromStorage;
    const isStudentInvite = Boolean(teacherCode);

    // Persist parent code from URL into localStorage so autoJoinPending can use it
    const parentCodeFromUrl = params.get("parent_code") || "";
    if (parentCodeFromUrl) {
        localStorage.setItem("pending_parent_code", parentCodeFromUrl);
    }

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        gender: "",
        password: "",
        confirmPassword: "",
        referralCode: (() => {
            const ref = params.get("ref");
            return ref ? ref.toUpperCase() : "";
        })(),
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [viewedLegal, setViewedLegal] = useState(false);

    // Shown after a teacher-invite signup so the kid can write down their ID
    const [studentIdInfo, setStudentIdInfo] = useState(null);

    const handleCheckboxChange = () => {
        if (viewedLegal) setAgreed(!agreed);
    };

    const handleViewLegal = () => {
        window.open("/privacy-policy", "_blank");
        setViewedLegal(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handlePhoneChange = (value) => {
        setFormData({ ...formData, phoneNumber: value });
    };

    const togglePassword = () => setShowPassword(!showPassword);
    const toggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

    // After any successful registration, consume pending invite codes
    const autoJoinPending = async (token) => {
        const teacherCode = localStorage.getItem("pending_teacher_code");
        if (teacherCode) {
            try {
                const res = await fetch(`${API_BASE_URL}/api/teachers/join/${teacherCode}`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                });
                if (res.ok) localStorage.removeItem("pending_teacher_code");
            } catch (e) {
                console.error("Auto-join teacher failed:", e);
            }
        }

        const parentCode = localStorage.getItem("pending_parent_code");
        if (parentCode) {
            try {
                const res = await fetch(`${API_BASE_URL}/api/parents/join/${parentCode}`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                });
                if (res.ok) localStorage.removeItem("pending_parent_code");
            } catch (e) {
                console.error("Auto-join parent failed:", e);
            }
        }
    };

    // ── Teacher-invite student registration ──────────────────────────────────
    const handleStudentSubmit = async (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) {
            toast.error("Passwords do not match!");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const response = await fetch(`${API_BASE_URL}/api/users/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    password: formData.password,
                    teacher_code: teacherCode,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem("token", data.token);
                localStorage.setItem("refreshToken", data.refreshToken);
                localStorage.setItem("fullName", data.fullName);
                localStorage.setItem("hasSeenModal", String(data.hasSeenModal));
                localStorage.setItem("accountType", data.accountType);
                const roleIdStudent = data.student_id || data.teacher_id || data.parent_id;
                if (roleIdStudent) localStorage.setItem("role_id", roleIdStudent);

                // teacher_code was already handled in the request body;
                // clear it and handle any pending parent code
                localStorage.removeItem("pending_teacher_code");
                await autoJoinPending(data.token);

                const displayId = data.student_id || data.user_id;
                setStudentIdInfo({ id: displayId, name: data.fullName });
            } else {
                toast.error(data.message || "Registration failed.");
                setError(data.message);
            }
        } catch (err) {
            console.error("Registration error:", err);
            toast.error("An unexpected error occurred.");
            setError("Check your internet connection");
        } finally {
            setLoading(false);
        }
    };

    // ── Standard registration ─────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) {
            toast.error("Passwords do not match!");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const { firstName, lastName, email, phoneNumber, password, gender, referralCode } = formData;

            const response = await fetch(`${API_BASE_URL}/api/users/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    firstName,
                    lastName,
                    email,
                    phoneNumber,
                    password,
                    gender,
                    referral_code: referralCode || null,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem("token", data.token);
                localStorage.setItem("refreshToken", data.refreshToken);
                localStorage.setItem("fullName", data.fullName);
                localStorage.setItem("hasSeenModal", String(data.hasSeenModal));
                // No role_id yet — that gets created after onboarding when accountType is chosen

                // Onboarding will pick the account type; navigate straight there.
                navigate("/onboarding");
            } else {
                toast.error(data.message || "Registration failed.");
                setError(data.message);
            }
        } catch (err) {
            console.error("Registration error:", err);
            toast.error("An unexpected error occurred.");
            setError("Check your internet connection");
        } finally {
            setLoading(false);
        }
    };

    // ── Student ID confirmation screen ────────────────────────────────────────
    if (studentIdInfo) {
        return (
            <div
                className="flex justify-center items-center min-h-screen"
                style={{
                    backgroundImage: `url(${bg})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundColor: "#2148C0",
                }}
            >
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 md:p-12 max-w-sm w-[90%] text-center">
                    <div className="w-16 h-16 bg-green-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl">🎉</span>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                        Welcome, {studentIdInfo.name}!
                    </h2>
                    <p className="text-white/70 text-sm mb-6">
                        {localStorage.getItem("accountType") === "teacher"
                            ? "Your teacher account is ready on TeeChaa."
                            : localStorage.getItem("accountType") === "parent"
                                ? "Your parent account is ready on TeeChaa."
                                : "Your student account is ready on TeeChaa."}
                    </p>

                    {/* ID card */}
                    <div className="bg-white/20 border-2 border-white/40 rounded-xl px-6 py-4 mb-2">
                        <p className="text-white/70 text-xs uppercase tracking-widest mb-1">Your Account ID</p>
                        <p className="text-white font-mono text-3xl font-bold tracking-wider">
                            {studentIdInfo.id}
                        </p>
                    </div>
                    <p className="text-yellow-200 text-xs mb-6 leading-relaxed">
                        Save this ID — you can use it to log in anytime along with your password.
                    </p>

                    <button
                        onClick={() => {
                            const type = localStorage.getItem("accountType");
                            if (type === "teacher") navigate("/teacher-dashboard");
                            else if (type === "parent") navigate("/parent-dashboard");
                            else navigate("/onboarding");
                        }}
                        className="w-full py-3 bg-white text-[#2148C0] font-bold rounded-xl hover:bg-gray-100 transition"
                    >
                        Got it, let's go!
                    </button>
                </div>
            </div>
        );
    }

    // ── Teacher-invite student form (simplified) ──────────────────────────────
    if (isStudentInvite) {
        return (
            <div
                className="flex justify-center items-center min-h-screen"
                style={{
                    backgroundImage: `url(${bg})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundColor: "#2148C0",
                }}
            >
                <div className="flex flex-col bg-transparent rounded-lg overflow-hidden w-[90%] md:w-[80%] max-w-md">
                    <div className="w-full p-8 flex flex-col justify-center">
                        <h2 className="text-3xl font-bold text-center mb-1 text-white">TeeChaa</h2>
                        <p className="text-center text-white/70 text-sm mb-6">
                            Your teacher has invited you — just enter your name and a password!
                        </p>

                        <form onSubmit={handleStudentSubmit} className="space-y-5">
                            {/* Names */}
                            <div className="md:flex md:space-x-4">
                                <input
                                    type="text"
                                    name="firstName"
                                    placeholder="First Name"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className="w-full md:w-1/2 p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-white"
                                    required
                                />
                                <input
                                    type="text"
                                    name="lastName"
                                    placeholder="Last Name (optional)"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="w-full md:w-1/2 p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-white mt-4 md:mt-0"
                                />
                            </div>

                            {/* Password */}
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="Choose a password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="w-full p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-white"
                                    required
                                />
                                <span
                                    onClick={togglePassword}
                                    className="absolute right-3 top-3.5 cursor-pointer text-white"
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </span>
                            </div>

                            {/* Confirm Password */}
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    placeholder="Confirm password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className="w-full p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-white"
                                    required
                                />
                                <span
                                    onClick={toggleConfirmPassword}
                                    className="absolute right-3 top-3.5 cursor-pointer text-white"
                                >
                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                </span>
                            </div>

                            {error && (
                                <p className="text-sm text-red-300 bg-white/10 px-3 py-2 rounded">{error}</p>
                            )}

                            <button
                                type="submit"
                                className="w-full bg-white text-[#2148C0] p-2 rounded-lg hover:bg-gray-200 transition duration-300"
                            >
                                {loading ? (
                                    <svg
                                        className="animate-spin h-5 w-5 text-[#2148C0] m-auto"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                    </svg>
                                ) : (
                                    "Join Class"
                                )}
                            </button>
                        </form>

                        <p className="mt-5 text-center text-white/60 text-xs">
                            Already have an account?{" "}
                            <a href="/login" className="text-white underline">Log In</a>
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ── Standard signup form ──────────────────────────────────────────────────
    return (
        <div
            className="flex justify-center items-center min-h-screen"
            style={{
                backgroundImage: `url(${bg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundColor: "#2148C0",
            }}
        >
            <div className="flex flex-col bg-transparent rounded-lg overflow-hidden w-[90%] md:w-[80%] max-w-4xl">
                <div className="w-full md:w-1/2 m-auto p-8 flex flex-col justify-center">
                    <h2 className="text-3xl font-bold text-center mb-6 text-white">TeeChaa</h2>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="md:flex md:space-x-4">
                            <input
                                type="text"
                                name="firstName"
                                placeholder="First Name"
                                value={formData.firstName}
                                onChange={handleChange}
                                className="w-full md:w-1/2 p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-white"
                                required
                            />
                            <input
                                type="text"
                                name="lastName"
                                placeholder="Last Name"
                                value={formData.lastName}
                                onChange={handleChange}
                                className="w-full md:w-1/2 p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-white mt-4 md:mt-0"
                                required
                            />
                        </div>

                        <input
                            type="email"
                            name="email"
                            placeholder="Email (optional)"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-white"
                        />

                        <input
                            type="text"
                            name="referralCode"
                            placeholder="Referral Code (optional)"
                            value={formData.referralCode}
                            onChange={handleChange}
                            className="w-full p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-white"
                        />

                        <PhoneInput
                            country={"ng"}
                            value={formData.phoneNumber}
                            onChange={handlePhoneChange}
                            inputStyle={{
                                width: "100%",
                                padding: "1rem",
                                paddingLeft: "3rem",
                                borderRadius: "0.5rem",
                                backgroundColor: "transparent",
                                color: "white",
                                border: "1px solid #fff",
                            }}
                            buttonStyle={{ backgroundColor: "transparent", border: "none" }}
                            inputClass="!placeholder-white"
                        />

                        <select
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            className="w-full p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-white"
                        >
                            <option value="" className="text-gray-600">Select Gender (optional)</option>
                            <option value="male" className="text-black">Male</option>
                            <option value="female" className="text-black">Female</option>
                            <option value="other" className="text-black">Other</option>
                        </select>

                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                placeholder="Password"
                                value={formData.password}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-white"
                                required
                            />
                            <span onClick={togglePassword} className="absolute right-3 top-3.5 cursor-pointer text-white">
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                        </div>

                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                placeholder="Confirm Password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-white"
                                required
                            />
                            <span onClick={toggleConfirmPassword} className="absolute right-3 top-3.5 cursor-pointer text-white">
                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-white text-[#2148C0] p-2 rounded-lg hover:bg-gray-200 transition duration-300"
                        >
                            {loading ? (
                                <svg className="animate-spin h-5 w-5 text-[#2148C0] m-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                </svg>
                            ) : (
                                "Sign Up"
                            )}
                        </button>
                    </form>

                    <div className="flex items-center my-5">
                        <div className="flex-1 h-px bg-gray-300"></div>
                        <span className="px-3 text-gray-300">OR</span>
                        <div className="flex-1 h-px bg-gray-300"></div>
                    </div>

                    <div className="w-full">
                        <SignUpWithGoogle />
                    </div>

                    {error && (
                        <p className="text-sm text-red-500 bg-white px-2 py-1 rounded mt-3 text-center">{error}</p>
                    )}

                    <div className="flex w-full items-start text-white gap-2 mt-4">
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={handleCheckboxChange}
                            disabled={!viewedLegal}
                            className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600 border-gray-300 rounded focus:ring focus:ring-blue-500"
                        />
                        <label className={`text-sm ${!viewedLegal ? "text-gray-400" : "text-white"}`}>
                            I have read and agree to the{" "}
                            <button type="button" onClick={handleViewLegal} className="text-blue-400 underline hover:text-blue-600">
                                Terms & Privacy Policy
                            </button>
                        </label>
                    </div>

                    <p className="mt-4 text-center text-gray-300">
                        Already have an account?{" "}
                        <a href="/login" className="text-white hover:underline">Log In</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SignupPage;
